import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(response.data);
          setToken(savedToken);
          setMaintenanceMode(false);
          // Check if password change is required from localStorage
          const savedMustChange = localStorage.getItem('mustChangePassword');
          setMustChangePassword(savedMustChange === 'true');
        } catch (error) {
          if (error.response?.status === 503 && error.response?.data?.maintenance) {
            setMaintenanceMode(true);
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('mustChangePassword');
            setToken(null);
          }
        }
      } else {
        // No token — check maintenance status for login page
        try {
          const res = await axios.get(`${API_URL}/maintenance-status`);
          setMaintenanceMode(res.data?.maintenance === true);
        } catch { /* ignore */ }
      }
      setLoading(false);
    };

    verifyToken();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      const { access_token, user: userData, must_change_password } = response.data;
      localStorage.setItem('token', access_token);
      if (must_change_password) {
        localStorage.setItem('mustChangePassword', 'true');
      }
      setToken(access_token);
      setUser(userData);
      setMustChangePassword(must_change_password || false);
      // After login, verify access (will 503 if not exempt)
      try {
        await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${access_token}` }
        });
        setMaintenanceMode(false);
      } catch (meErr) {
        if (meErr.response?.status === 503 && meErr.response?.data?.maintenance) {
          setMaintenanceMode(true);
          return { user: userData, mustChangePassword: false, maintenance: true };
        }
      }
      return { user: userData, mustChangePassword: must_change_password || false };
    } catch (error) {
      throw error;
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    await axios.post(`${API_URL}/auth/change-password`, 
      { current_password: currentPassword, new_password: newPassword },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    localStorage.removeItem('mustChangePassword');
    setMustChangePassword(false);
  };

  const register = async (name, email, password) => {
    await axios.post(`${API_URL}/auth/register`, { name, email, password });
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('mustChangePassword');
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, getAuthHeader, mustChangePassword, changePassword, maintenanceMode }}>
      {children}
    </AuthContext.Provider>
  );
};
